/**
 * Site-wide Webflow helpers and animations.
 *
 * Requires GSAP globally for animation features. Scroll animations additionally
 * use ScrollTrigger when it is loaded. WhatsApp QR generation requires kjua.
 */

function onReady(callback) {
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", callback);
  } else {
    callback();
  }
}

function imagesLoaded(element) {
  const images = [...element.querySelectorAll("img")];
  let settledCount = 0;

  return new Promise((resolve) => {
    if (!images.length) {
      resolve();
      return;
    }

    const markSettled = () => {
      settledCount += 1;
      if (settledCount === images.length) resolve();
    };

    images.forEach((img) => {
      if (img.complete) {
        markSettled();
        return;
      }

      img.addEventListener("load", markSettled, { once: true });
      img.addEventListener("error", markSettled, { once: true });
    });
  });
}

function revealWithoutGsap(selector) {
  document.querySelectorAll(selector).forEach((element) => {
    element.style.opacity = "1";
    element.style.transform = "translateY(0)";
  });
}

async function initHeroNotificationImages() {
  const gsap = window.gsap;
  const wrapper = document.querySelector(
    ".uui-heroheader10_notification-image-wrapper",
  );
  if (!wrapper) return;

  const images = wrapper.querySelectorAll("img");
  if (!images.length) return;

  if (!gsap) {
    revealWithoutGsap(".uui-heroheader10_notification-image-wrapper img");
    return;
  }

  await imagesLoaded(wrapper);

  gsap.fromTo(
    images,
    { y: 50, autoAlpha: 0 },
    {
      y: 0,
      autoAlpha: 1,
      duration: 0.8,
      stagger: 0.2,
      ease: "power2.out",
      delay: 0.5,
    },
  );
}

function initPageAnimations() {
  const gsap = window.gsap;
  const ScrollTrigger = window.ScrollTrigger;

  if (!gsap) {
    revealWithoutGsap('[animation="load"], [animation="scroll"]');
    return;
  }

  gsap.to('[animation="load"]', {
    opacity: 1,
    y: 0,
    duration: 1,
    stagger: 0.2,
    ease: "power2.out",
  });

  if (!ScrollTrigger) {
    gsap.to('[animation="scroll"]', {
      opacity: 1,
      y: 0,
      duration: 0.6,
      ease: "power2.out",
    });
    return;
  }

  gsap.registerPlugin(ScrollTrigger);

  gsap.utils.toArray('[animation="scroll"]').forEach((element) => {
    gsap.fromTo(
      element,
      { opacity: 0, y: 20 },
      {
        scrollTrigger: {
          trigger: element,
          start: "top 80%",
          end: "bottom 20%",
        },
        opacity: 1,
        y: 0,
        duration: 1,
        ease: "power2.out",
      },
    );
  });
}

function initCurrentYear() {
  const yearEl = document.querySelector('span[show="year"]');
  if (yearEl) yearEl.textContent = String(new Date().getFullYear());
}

function initUtilityCopyMenus() {
  const gsap = window.gsap;

  document.querySelectorAll(".utility-1_component").forEach((component) => {
    if (component.hasAttribute("data-utility-1")) return;
    component.setAttribute("data-utility-1", "");

    const link = component.querySelector(".utility-1_link");
    const trigger = component.querySelector(".utility-1_trigger");
    const position = component.querySelector(".utility-1_position");
    const menu = component.querySelector(".utility-1_menu");
    if (!link || !trigger || !position || !menu) return;

    const items = [...menu.querySelectorAll("[role='menuitem']")];
    if (!items.length) return;

    let isOpen = false;

    if (!gsap) {
      position.style.display = "none";
    } else {
      gsap.set(position, { display: "none" });
    }

    const tl = gsap
      ? gsap.timeline({ paused: true, defaults: { duration: 0.3 } })
      : null;

    if (tl) {
      tl.set(position, { display: "block" });
      tl.from(position, { autoAlpha: 0, y: "1.5rem", duration: 0.5 });
      tl.from(items, { autoAlpha: 0, y: "1rem", stagger: 0.06 }, "<");
    }

    function openMenu() {
      if (isOpen) return;
      isOpen = true;
      trigger.setAttribute("aria-expanded", "true");

      if (tl) {
        tl.timeScale(1).play();
      } else {
        position.style.display = "block";
      }

      window.setTimeout(() => items[0]?.focus(), 100);
    }

    function closeMenu({ returnFocus = false } = {}) {
      if (!isOpen) return;
      isOpen = false;
      trigger.setAttribute("aria-expanded", "false");

      if (tl) {
        tl.timeScale(1.5).reverse();
      } else {
        position.style.display = "none";
      }

      if (returnFocus) trigger.focus();
    }

    link.addEventListener("contextmenu", (event) => {
      event.preventDefault();
      openMenu();
    });

    trigger.addEventListener("click", openMenu);

    document.addEventListener("mousedown", (event) => {
      if (isOpen && !component.contains(event.target)) closeMenu();
    });

    menu.addEventListener("keydown", (event) => {
      const index = items.indexOf(document.activeElement);

      if (event.key === "ArrowDown") {
        event.preventDefault();
        items[(index + 1) % items.length]?.focus();
      } else if (event.key === "ArrowUp") {
        event.preventDefault();
        items[(index - 1 + items.length) % items.length]?.focus();
      } else if (event.key === "Escape") {
        event.preventDefault();
        closeMenu({ returnFocus: true });
      } else if (event.key === "Tab") {
        closeMenu();
      }
    });

    items.forEach((item) => {
      item.addEventListener("mouseenter", () => item.focus());
      item.addEventListener("click", () => {
        const text = item.querySelector(".utility-1_text");
        const svg = item.querySelector(".utility-1_svg-to-copy");
        if (!text || !svg) return;

        const originalText = text.textContent;
        navigator.clipboard
          ?.writeText(svg.outerHTML.trim())
          .catch((error) => console.warn("[utility-copy] copy failed", error));

        text.textContent = "Copied";
        window.setTimeout(() => {
          closeMenu({ returnFocus: true });
          window.setTimeout(() => {
            text.textContent = originalText;
          }, 1000);
        }, 1000);
      });
    });
  });
}

function initWhatsAppModal() {
  const modal = document.querySelector("[data-whatsapp-modal]");
  if (!modal) return;

  const url = (modal.getAttribute("data-whatsapp-modal") || "").trim();
  if (!url) return;

  if (!window.kjua) {
    console.warn("[whatsapp-modal] kjua missing, QR code skipped");
    return;
  }

  const svg = window.kjua({
    text: url,
    render: "svg",
    crisp: true,
    minVersion: 1,
    ecLevel: "M",
    size: 540,
    fill: "#000000",
    back: "#FFFFFF",
    rounded: 0,
  });

  svg.removeAttribute("width");
  svg.removeAttribute("height");
  svg.removeAttribute("style");

  modal
    .querySelectorAll("[data-whatsapp-modal-qr-canvas]")
    .forEach((placeholder, index) => {
      const node = index === 0 ? svg : svg.cloneNode(true);
      placeholder.appendChild(node);
    });

  document.querySelectorAll("[data-whatsapp-modal-link]").forEach((linkEl) => {
    linkEl.setAttribute("href", url);
    linkEl.setAttribute("target", "_blank");
    linkEl.setAttribute("rel", "noopener");
  });

  document
    .querySelectorAll("[data-whatsapp-modal-toggle]")
    .forEach((button) => {
      button.addEventListener("click", () => {
        const isActive =
          modal.getAttribute("data-whatsapp-modal-status") === "active";
        modal.setAttribute(
          "data-whatsapp-modal-status",
          isActive ? "not-active" : "active",
        );
      });
    });

  document.addEventListener("keydown", (event) => {
    if (event.key === "Escape") {
      modal.setAttribute("data-whatsapp-modal-status", "not-active");
    }
  });
}

function initTallyEmbeds() {
  if (window.Tally?.loadEmbeds) {
    window.Tally.loadEmbeds();
  }
}

onReady(() => {
  initHeroNotificationImages();
  initPageAnimations();
  initCurrentYear();
  initUtilityCopyMenus();
  initWhatsAppModal();
  initTallyEmbeds();
});
